
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import MobileBottomNav from '../../components/MobileBottomNav';
import { UAParser } from 'ua-parser-js';

const ActivityLogPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const [timeRange, setTimeRange] = useState('week');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [cards, setCards] = useState({
    pageViews: 0,
    uniqueVisitors: 0,
    newVisitors: 0,
    returningVisitors: 0,
    onlineNow: 0,
  });

  const [presence, setPresence] = useState({
    onlineNow: 0,
    totalTracked: 0,
    lastSeen: [],
  });

  const [expandedVkey, setExpandedVkey] = useState(null);

  const [chartData, setChartData] = useState({
    siteActivity: [
      { x: 0, y: 260 },
      { x: 100, y: 230 },
      { x: 200, y: 180 },
      { x: 300, y: 140 },
      { x: 400, y: 190 },
      { x: 500, y: 120 },
      { x: 600, y: 80 },
    ],
  });

  const [hoveredPoint, setHoveredPoint] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsHasMore, setEventsHasMore] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const EVENTS_PAGE_SIZE = 20;

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [autoRefreshEvery, setAutoRefreshEvery] = useState(60_000);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [timeTick, setTimeTick] = useState(0);

  const [presenceQuery, setPresenceQuery] = useState('');
  const [presenceType, setPresenceType] = useState('all');
  const [presenceSort, setPresenceSort] = useState('lastSeenDesc');
  const [presenceShowCount, setPresenceShowCount] = useState(10);

  const [eventsQuery, setEventsQuery] = useState('');
  const [eventsType, setEventsType] = useState('all');
  const [eventsUserType, setEventsUserType] = useState('all');

  const uaCacheRef = useRef(new Map());

  const toValidDate = (val) => {
    if (!val && val !== 0) return null;
    if (val instanceof Date) return isNaN(val) ? null : val;
    if (typeof val === 'number') {
      const dNum = new Date(val);
      return isNaN(dNum) ? null : dNum;
    }
    if (typeof val === 'string') {
      const try1 = new Date(val);
      if (!isNaN(try1)) return try1;
      const try2 = new Date(val + 'Z');
      if (!isNaN(try2)) return try2;
    }
    return null;
  };

  const timeAgoClient = (iso, opts = {}) => {
    const { isModified = false, now: nowOverride } = opts;
    try {
      if (!iso) return '';
      const then = toValidDate(iso);
      if (!then) return '';
      const now = nowOverride instanceof Date ? nowOverride : new Date();
      const diffMs = now - then;
      if (!isFinite(diffMs)) return '';
      const differenceInMinutes = Math.floor(diffMs / 60000);
      if (differenceInMinutes < 0) return '';
      let timeText = '';
      if (differenceInMinutes < 1) {
        timeText = 'الآن';
      } else if (differenceInMinutes < 60) {
        if (differenceInMinutes === 1) timeText = 'قبل دقيقة';
        else if (differenceInMinutes === 2) timeText = 'قبل دقيقتين';
        else if (differenceInMinutes <= 10) timeText = `قبل ${differenceInMinutes} دقايق`;
        else timeText = `قبل ${differenceInMinutes} دقيقة`;
      } else {
        const differenceInHours = Math.floor(differenceInMinutes / 60);
        const differenceInDays = Math.floor(differenceInHours / 24);
        const differenceInMonths = Math.floor(differenceInDays / 30);
        const differenceInYears = Math.floor(differenceInDays / 365);
        if (differenceInHours < 24) {
          if (differenceInHours === 1) timeText = 'قبل ساعة';
          else if (differenceInHours === 2) timeText = 'قبل ساعتين';
          else if (differenceInHours <= 10) timeText = `قبل ${differenceInHours} ساعات`;
          else timeText = `قبل ${differenceInHours} ساعة`;
        } else if (differenceInDays < 31) {
          if (differenceInDays === 1) timeText = 'قبل يوم';
          else if (differenceInDays === 2) timeText = 'قبل يومين';
          else if (differenceInDays <= 10) timeText = `قبل ${differenceInDays} أيام`;
          else timeText = `قبل ${differenceInDays} يوم`;
        } else if (differenceInMonths < 12) {
          if (differenceInMonths === 1) timeText = 'قبل شهر';
          else if (differenceInMonths === 2) timeText = 'قبل شهرين';
          else if (differenceInMonths <= 10) timeText = `قبل ${differenceInMonths} شهور`;
          else timeText = `قبل ${differenceInMonths} شهر`;
        } else {
          if (differenceInYears === 1) timeText = 'مضى سنة';
          else if (differenceInYears === 2) timeText = 'مضى سنتين';
          else if (differenceInYears <= 10) timeText = `مضى ${differenceInYears} سنوات`;
          else timeText = 'مضى أكثر من 10 سنوات';
        }
      }
      if (isModified) timeText = `معدلة: ${timeText}`;
      return timeText;
    } catch {
      return '';
    }
  };

  const displayIp = (rawIp = '', ipHash = '') => {
    const ip = String(rawIp || '').trim();
    if (ip) return ip;
    if (ipHash) return `hash:${String(ipHash).slice(0, 16)}…`;
    return '';
  };

  const safeCopy = async (txt) => {
    const text = String(txt || '');
    if (!text) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {}
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '-9999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  };

  const parseUA = useCallback(
    (ua = '') => {
      const key = String(ua || '');
      const cached = uaCacheRef.current.get(key);
      if (cached) return cached;

      const parser = new UAParser(key);
      const os = parser.getOS();
      const device = parser.getDevice();
      const browser = parser.getBrowser();

      const osNameRaw = String(os?.name || '').toLowerCase();
      const osVersion = os?.version ? String(os.version) : '';
      let osLabel = 'غير معروف';

      if (osNameRaw.includes('ios')) osLabel = 'iOS';
      else if (osNameRaw.includes('android')) osLabel = 'Android';
      else if (osNameRaw.includes('mac os')) osLabel = 'macOS';
      else if (osNameRaw.includes('windows')) osLabel = 'Windows';
      else if (osNameRaw.includes('linux')) osLabel = 'Linux';
      else if (os?.name) osLabel = String(os.name);

      const deviceType = device?.type ? String(device.type) : '';
      const deviceVendor = device?.vendor ? String(device.vendor) : '';
      const deviceModel = device?.model ? String(device.model) : '';

      let deviceLabel = 'غير معروف';
      if (deviceType) {
        if (deviceType === 'mobile') deviceLabel = 'هاتف';
        else if (deviceType === 'tablet') deviceLabel = 'تابلت';
        else if (deviceType === 'console') deviceLabel = 'جهاز ألعاب';
        else if (deviceType === 'smarttv') deviceLabel = 'شاشة ذكية';
        else if (deviceType === 'wearable') deviceLabel = 'ساعة/قابل للارتداء';
        else deviceLabel = deviceType;
      } else {
        const low = key.toLowerCase();
        if (low.includes('mobile') || low.includes('iphone') || low.includes('android')) deviceLabel = 'هاتف';
        else if (low.includes('ipad') || low.includes('tablet')) deviceLabel = 'تابلت';
        else if (low.includes('windows') || low.includes('macintosh') || low.includes('linux')) deviceLabel = 'كمبيوتر';
      }

      const browserName = browser?.name ? String(browser.name) : 'غير معروف';
      const browserVersion = browser?.version ? String(browser.version) : '';

      const osFull = osVersion ? `${osLabel} ${osVersion}` : osLabel;
      const browserFull = browserVersion ? `${browserName} ${browserVersion}` : browserName;

      const deviceFull = (() => {
        const bits = [deviceLabel];
        const extra = [deviceVendor, deviceModel].filter(Boolean).join(' ');
        if (extra) bits.push(extra);
        return bits.join(' - ');
      })();

      const out = { osLabel, osFull, deviceLabel, deviceFull, browserName, browserFull };
      uaCacheRef.current.set(key, out);
      return out;
    },
    []
  );

  const LPX = (x) => 55 + (x / 600) * 630;
  const LPY = (y) => 10 + (y / 320) * 295;
  const L_BOTTOM = 305;

  const smoothLinePath = (pts) => {
    if (!pts || pts.length < 2) return '';
    let d = `M${LPX(pts[0].x).toFixed(1)},${LPY(pts[0].y).toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[Math.max(0, i-2)];
      const p1 = pts[i-1];
      const p2 = pts[i];
      const p3 = pts[Math.min(pts.length-1, i+1)];
      const cp1x = LPX(p1.x) + (LPX(p2.x)-LPX(p0.x))/6;
      const cp1y = LPY(p1.y) + (LPY(p2.y)-LPY(p0.y))/6;
      const cp2x = LPX(p2.x) - (LPX(p3.x)-LPX(p1.x))/6;
      const cp2y = LPY(p2.y) - (LPY(p3.y)-LPY(p1.y))/6;
      d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${LPX(p2.x).toFixed(1)},${LPY(p2.y).toFixed(1)}`;
    }
    return d;
  };

  const smoothLineArea = (pts) => {
    if (!pts || pts.length < 2) return '';
    return `${smoothLinePath(pts)} L${LPX(pts[pts.length-1].x).toFixed(1)},${L_BOTTOM} L${LPX(pts[0].x).toFixed(1)},${L_BOTTOM} Z`;
  };

  const lXLabels = (() => {
    if (timeRange === 'week')    return ['أحد','إثنين','ثلاثاء','أربعاء','خميس','جمعة','سبت'];
    if (timeRange === 'month')   return ['الأول','','الثاني','','الثالث','','الرابع'];
    if (timeRange === 'quarter') return ['شهر 1','','شهر 2','','شهر 3','','الآن'];
    return ['يناير','مارس','مايو','يوليو','سبتمبر','نوفمبر','الآن'];
  })();

  const CountUp = ({ value = 0, duration = 800 }) => {
    const [n, setN] = useState(0);
    const startRef = useRef(0);
    useEffect(() => {
      const from = startRef.current;
      const to = Number(value) || 0;
      const t0 = performance.now();
      let raf;
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const cur = Math.round(from + (to - from) * p);
        setN(cur);
        if (p < 1) raf = requestAnimationFrame(tick);
        else startRef.current = to;
      };
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [value, duration]);
    return <>{n.toLocaleString('en-US')}</>;
  };

  const defaultSiteActivity = [
    { x: 0, y: 260 },
    { x: 100, y: 230 },
    { x: 200, y: 180 },
    { x: 300, y: 140 },
    { x: 400, y: 190 },
    { x: 500, y: 120 },
    { x: 600, y: 80 },
  ];

  const handleTimeRangeChange = (range) => {
    if (range === timeRange) return;
    setTimeRange(range);
  };

  const fetchDashboardData = useCallback(
    async (range) => {
      try {
        setLoading(true);
        setErrorMsg('');
        const resp = await fetch(`/api/dashboard/evaluations?timeRange=${range}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const api = await resp.json();

        setCards((prev) => ({ ...prev, ...(api.cards || {}) }));
        setPresence(api.presence || { onlineNow: 0, totalTracked: 0, lastSeen: [] });

        setChartData({
          siteActivity: api?.series?.siteActivity?.length ? api.series.siteActivity : defaultSiteActivity,
        });

        setLastUpdatedAt(new Date().toISOString());
      } catch (e) {
        console.error('Failed to load dashboard data', e);
        setErrorMsg('تعذر تحميل بيانات النشاط، يتم عرض بيانات تجريبية.');
        setChartData({ siteActivity: defaultSiteActivity });
        setLastUpdatedAt(new Date().toISOString());
      } finally {
        setLoading(false);
      }
    },
    [defaultSiteActivity]
  );

  const fetchEvents = useCallback(async (page = 0) => {
    try {
      setEventsLoading(true);
      const limit = EVENTS_PAGE_SIZE;
      const offset = page * limit;

      const resp = await fetch(`/api/admin/site-events?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const list = await resp.json();

      if (page === 0) {
        setEvents(Array.isArray(list) ? list : []);
      } else {
        setEvents((prev) => {
          const seenIds = new Set(prev.map((e) => e.id));
          const fresh = (Array.isArray(list) ? list : []).filter((e) => !seenIds.has(e.id));
          return [...prev, ...fresh];
        });
      }

      if (!Array.isArray(list) || list.length < limit) setEventsHasMore(false);
      else setEventsHasMore(true);

      setEventsPage(page);
    } catch (e) {
      console.error('Failed to load site events', e);
      if (page === 0) {
        setEvents([]);
        setEventsHasMore(false);
      }
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await fetchDashboardData(timeRange);
    await fetchEvents(0);
  }, [fetchDashboardData, fetchEvents, timeRange]);

  useEffect(() => {
    fetchDashboardData(timeRange);
  }, [timeRange, fetchDashboardData]);

  useEffect(() => {
    if (!autoRefreshEnabled) return;
    const id = setInterval(() => fetchDashboardData(timeRange), autoRefreshEvery);
    return () => clearInterval(id);
  }, [autoRefreshEnabled, autoRefreshEvery, fetchDashboardData, timeRange]);

  useEffect(() => {
    const id = setInterval(() => setTimeTick((x) => x + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchEvents(0);
  }, [fetchEvents]);

  const metricCardBase = {
    background: 'radial-gradient(55% 90% at 50% -5%,rgba(40,227,218,.18) 0%,rgba(40,227,218,.04) 55%,rgba(8,20,35,.9) 100%)',
    boxShadow: '0 4px 20px rgba(0,0,0,.3)',
    borderRadius: '18px',
    border: '1px solid rgba(40,227,218,.22)',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform .2s, box-shadow .2s',
    cursor: 'default',
    backdropFilter: 'blur(6px)',
  };

  const onMetricHoverIn = (e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,.55)';
  };

  const onMetricHoverOut = (e) => {
    e.currentTarget.style.transform = 'translateY(0px)';
    e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,.35)';
  };

  const presenceList = Array.isArray(presence.lastSeen) ? presence.lastSeen : [];

  const ratioOnline =
    presence.totalTracked && presence.totalTracked > 0
      ? Math.round((presence.onlineNow / presence.totalTracked) * 100)
      : 0;

  const rangeLabel = (() => {
    switch (timeRange) {
      case 'month':
        return 'آخر 30 يوم';
      case 'quarter':
        return 'آخر 3 أشهر';
      case 'year':
        return 'آخر سنة';
      default:
        return 'آخر 7 أيام';
    }
  })();

  const labelForPoint = (idx) => {
    switch (timeRange) {
      case 'month':
        return `اليوم ${idx + 1}`;
      case 'quarter':
        return `الأسبوع ${idx + 1}`;
      case 'year':
        return `الشهر ${idx + 1}`;
      default:
        return `اليوم ${idx + 1}`;
    }
  };

  const percentForPoint = (y) => {
    const raw = 100 - Math.round((y / 320) * 100);
    if (!isFinite(raw)) return 0;
    return Math.max(0, Math.min(100, raw));
  };

  const lastUpdatedText = useMemo(() => {
    if (!lastUpdatedAt) return 'لم يتم التحديث بعد';
    return `${timeAgoClient(lastUpdatedAt, { now: new Date() })}`;
  }, [lastUpdatedAt, timeTick]);

  const normalize = (s) => String(s || '').trim().toLowerCase();

  const getCountry = (obj) =>
    obj?.country ||
    obj?.geo?.country ||
    obj?.extra?.country ||
    obj?.extra?.geo?.country ||
    'غير معروفة';

  const getCity = (obj) =>
    obj?.city ||
    obj?.geo?.city ||
    obj?.extra?.city ||
    obj?.extra?.geo?.city ||
    '';

  const getRegion = (obj) =>
    obj?.region ||
    obj?.geo?.region ||
    obj?.extra?.region ||
    obj?.extra?.geo?.region ||
    '';

  const getISP = (obj) =>
    obj?.isp ||
    obj?.geo?.isp ||
    obj?.extra?.isp ||
    obj?.extra?.geo?.isp ||
    '';

  const isRegisteredSession = (s) =>
    Boolean(s?.userId || s?.user_id || s?.email || s?.username || s?.displayName);

  const effectiveUserId = (s) => s?.userId || s?.user_id || null;

  const presenceAnalytics = useMemo(() => {
    const topN = (map, n = 3) =>
      Array.from(map.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([k, v]) => ({ k, v }));

    const osMap = new Map();
    const deviceMap = new Map();
    const browserMap = new Map();

    for (const s of presenceList) {
      const ua = s?.userAgent || s?.user_agent || '';
      const info = parseUA(ua);
      const os = info?.osLabel || 'غير معروف';
      const dev = info?.deviceLabel || 'غير معروف';
      const br = info?.browserName || 'غير معروف';

      osMap.set(os, (osMap.get(os) || 0) + 1);
      deviceMap.set(dev, (deviceMap.get(dev) || 0) + 1);
      browserMap.set(br, (browserMap.get(br) || 0) + 1);
    }

    return {
      osTop: topN(osMap),
      deviceTop: topN(deviceMap),
      browserTop: topN(browserMap),
    };
  }, [presenceList, parseUA]);

  const filteredPresence = useMemo(() => {
    const q = normalize(presenceQuery);
    let list = presenceList;

    if (presenceType !== 'all') {
      list = list.filter((s) => (presenceType === 'registered' ? isRegisteredSession(s) : !isRegisteredSession(s)));
    }

    if (q) {
      list = list.filter((s) => {
        const name =
          s?.displayName ||
          s?.username ||
          (s?.email ? String(s.email).split('@')[0] : 'مستخدم مجهول');
        const email = s?.email || '';
        const country = getCountry(s);
        const city = getCity(s);
        const region = getRegion(s);
        const isp = getISP(s);
        const ua = s?.userAgent || s?.user_agent || '';
        const uaInfo = parseUA(ua);
        const blob = [
          name,
          email,
          country,
          city,
          region,
          isp,
          uaInfo?.osFull,
          uaInfo?.deviceFull,
          uaInfo?.browserFull,
        ]
          .filter(Boolean)
          .join(' ');
        return normalize(blob).includes(q);
      });
    }

    const sorted = [...list].sort((a, b) => {
      const da = toValidDate(a?.lastSeen) || new Date(0);
      const db = toValidDate(b?.lastSeen) || new Date(0);
      if (presenceSort === 'lastSeenAsc') return da - db;
      return db - da;
    });

    return sorted;
  }, [presenceList, presenceQuery, presenceType, presenceSort, parseUA]);

  const presenceShown = useMemo(() => filteredPresence.slice(0, Math.max(1, presenceShowCount)), [filteredPresence, presenceShowCount]);

  const eventsTypesSet = useMemo(() => {
    const set = new Set();
    for (const e of events) {
      if (e?.eventType) set.add(String(e.eventType));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const q = normalize(eventsQuery);
    let list = events;

    if (eventsType !== 'all') {
      list = list.filter((e) => String(e?.eventType || '') === String(eventsType));
    }

    if (eventsUserType !== 'all') {
      list = list.filter((e) => {
        const reg = Boolean(e?.userId || e?.user_id || e?.email || e?.userEmail || e?.username || e?.displayName);
        return eventsUserType === 'registered' ? reg : !reg;
      });
    }

    if (q) {
      list = list.filter((e) => {
        const pageTitle = e?.pageTitle || '';
        const pagePath = e?.pagePath || '';
        const country = getCountry(e);
        const city = getCity(e);
        const region = getRegion(e);
        const isp = getISP(e);
        const email = e?.email || e?.userEmail || '';
        const user = e?.username || e?.displayName || '';
        const ua = e?.userAgent || e?.user_agent || '';
        const uaInfo = parseUA(ua);
        const blob = [pageTitle, pagePath, country, city, region, isp, email, user, uaInfo?.osFull, uaInfo?.deviceFull, uaInfo?.browserFull]
          .filter(Boolean)
          .join(' ');
        return normalize(blob).includes(q);
      });
    }

    return list;
  }, [events, eventsQuery, eventsType, eventsUserType, parseUA]);

  const exportCsv = (rows, filename = 'export.csv') => {
    try {
      const safe = (v) => {
        const s = String(v ?? '');
        const needs = /[",\n\r]/.test(s);
        const t = s.replace(/"/g, '""');
        return needs ? `"${t}"` : t;
      };
      const headers = Object.keys(rows?.[0] || {});
      if (!headers.length) return;

      const csv = [
        headers.map(safe).join(','),
        ...rows.map((r) => headers.map((h) => safe(r[h])).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const exportPresenceCsv = () => {
    const rows = filteredPresence.map((s, idx) => {
      const name =
        s?.displayName ||
        s?.username ||
        (s?.email ? String(s.email).split('@')[0] : 'مستخدم مجهول');
      const email = s?.email || '';
      const lastSeen = toValidDate(s?.lastSeen)?.toISOString?.() || String(s?.lastSeen || '');
      const country = getCountry(s);
      const city = getCity(s);
      const region = getRegion(s);
      const isp = getISP(s);
      const ua = s?.userAgent || s?.user_agent || '';
      const uaInfo = parseUA(ua);
      const rawIpVal = s?.ip || s?.ipAddress || s?.ip_address || '';
      const ipHash = s?.ipHash || s?.ip_hash || '';
      const reg = isRegisteredSession(s) ? 'registered' : 'guest';
      const uid = effectiveUserId(s) ? String(effectiveUserId(s)) : '';
      return {
        index: idx + 1,
        sessionType: reg,
        name,
        email,
        userId: uid,
        lastSeen,
        country,
        region,
        city,
        isp,
        ip: rawIpVal,
        ipHash,
        os: uaInfo.osFull,
        device: uaInfo.deviceFull,
        browser: uaInfo.browserFull,
      };
    });
    exportCsv(rows, `presence_${timeRange}.csv`);
  };

  const exportEventsCsv = () => {
    const rows = filteredEvents.map((e, idx) => {
      const createdAt = toValidDate(e?.createdAt)?.toISOString?.() || String(e?.createdAt || '');
      const pageTitle = e?.pageTitle || '';
      const pagePath = e?.pagePath || '';
      const eventType = e?.eventType || '';
      const country = getCountry(e);
      const city = getCity(e);
      const region = getRegion(e);
      const isp = getISP(e);
      const ua = e?.userAgent || e?.user_agent || '';
      const uaInfo = parseUA(ua);
      const rawIpVal = e?.ip || e?.ipAddress || e?.ip_address || '';
      const ipHash = e?.ipHash || e?.ip_hash || '';
      const userId = e?.userId || e?.user_id || '';
      const user = e?.email || e?.userEmail || e?.username || e?.displayName || '';
      const reg = Boolean(userId || user) ? 'registered' : 'guest';
      const referrer = e?.referrer || '';
      return {
        index: idx + 1,
        eventType,
        createdAt,
        sessionType: reg,
        userId: userId ? String(userId) : '',
        user,
        pageTitle,
        pagePath,
        country,
        region,
        city,
        isp,
        ip: rawIpVal,
        ipHash,
        os: uaInfo.osFull,
        device: uaInfo.deviceFull,
        browser: uaInfo.browserFull,
        referrer,
      };
    });
    exportCsv(rows, `site_events_${timeRange}.csv`);
  };

  return (
    <div
      className="min-h-screen text-white bg-transparent"
      style={{
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'row',
        background:
          'linear-gradient(0deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40,227,218,0.16) 0%, rgba(40,227,218,0) 100%), #081423',
      }}
    >
      <div className="hidden md:block sticky top-0 h-screen z-[200]">
        <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />
      </div>

      <div className="md:hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />
      </div>

      <div className="flex-1 flex flex-col md:ml-0" style={{ width: '100vw', marginLeft: isMobile ? '0' : 'auto' }}>
        <div
          className="flex-1 p-6 pb-20 md:pb-6"
          style={{
            maxWidth: '100%',
            padding: isMobile ? '16px 12px 80px' : isTablet ? '20px 24px 24px' : '24px 48px 32px',
          }}
        >
          <nav style={{
            display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-evenly',
            flexWrap: 'wrap', gap: isMobile ? '6px' : '4px',
            padding: isMobile ? '10px 12px' : '0 16px',
            height: isMobile ? 'auto' : '52px',
            background: 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,.2) 0%,rgba(40,227,218,0) 100%),rgba(9,28,43,.6)',
            border: '1px solid rgba(40,227,218,.2)',
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
            marginBottom: '24px',
          }}>
            {[
              { label: 'نظرة عامة',           path: '/dashboard' },
              { label: 'إدارة المستخدمين',    path: '/user-management' },
              { label: 'سجل النشاط',          active: true },
              { label: 'الإبلاغات',           path: '/reports' },
              { label: 'إضافة مقرر / محاضر', path: '/add-course-lecturer' },
              { label: 'إدارة المقررات',      path: '/manage-courses-lecturers' },
            ].map((item, i) => (
              <React.Fragment key={item.label}>
                {i > 0 && !isMobile && <span style={{ color: 'rgba(255,255,255,.15)', fontSize: '18px', userSelect: 'none' }}>|</span>}
                <span
                  onClick={() => item.path && navigate(item.path)}
                  style={{
                    fontFamily: 'Alexandria,sans-serif', fontSize: isMobile ? '12px' : '14px',
                    fontWeight: item.active ? 800 : 500, color: item.active ? '#7DF9E8' : '#94a3b8',
                    cursor: item.path ? 'pointer' : 'default',
                    padding: isMobile ? '5px 10px' : '0 4px', borderRadius: isMobile ? '8px' : 0,
                    background: item.active && isMobile ? 'rgba(40,227,218,.1)' : 'transparent',
                    transition: 'color .15s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (item.path) e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { if (item.path) e.currentTarget.style.color = '#94a3b8'; }}
                >{item.label}</span>
              </React.Fragment>
            ))}
          </nav>

          <div className="w-full max-w-[1108px] mx-auto mb-6">
            <div
              className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
              style={{
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                border: '1px solid rgba(40,227,218,.22)',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
                direction: 'rtl',
              }}
            >
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={refreshAll}
                  className="px-4 py-2 rounded-[14px] text-[13px] font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.6)',
                    background: 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
                    color: '#081423',
                    boxShadow: '0 0 18px rgba(40,227,218,.45)',
                  }}
                >
                  تحديث الآن
                </button>

                <button
                  onClick={() => {
                    setAutoRefreshEnabled((v) => !v);
                  }}
                  className="px-4 py-2 rounded-[14px] text-[13px] font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.35)',
                    background: autoRefreshEnabled ? 'rgba(40,227,218,.18)' : 'transparent',
                    color: '#D0E3F0',
                  }}
                >
                  التحديث التلقائي: {autoRefreshEnabled ? 'مفعل' : 'متوقف'}
                </button>

                <select
                  value={autoRefreshEvery}
                  onChange={(e) => setAutoRefreshEvery(Number(e.target.value) || 60_000)}
                  className="px-3 py-2 rounded-[14px] text-[13px] font-bold outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.35)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#D0E3F0',
                    direction: 'rtl',
                  }}
                >
                  <option value={30_000}>كل 30 ثانية</option>
                  <option value={60_000}>كل دقيقة</option>
                  <option value={120_000}>كل دقيقتين</option>
                  <option value={300_000}>كل 5 دقائق</option>
                </select>

                <div
                  className="px-3 py-2 rounded-[14px] text-[12px]"
                  style={{
                    border: '1px solid rgba(40,227,218,.20)',
                    background: 'rgba(9,28,43,.45)',
                    color: '#B8C5CE',
                  }}
                >
                  آخر تحديث: <span style={{ color: '#E8F5FF' }}>{loading ? 'يتم التحديث…' : lastUpdatedText}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {errorMsg ? (
                  <div className="px-3 py-2 rounded-[14px] text-[12px]" style={{ background: 'rgba(246,65,79,.12)', border: '1px solid rgba(246,65,79,.35)', color: '#FFC9CF' }}>
                    {errorMsg}
                  </div>
                ) : (
                  <div className="px-3 py-2 rounded-[14px] text-[12px]" style={{ background: 'rgba(40,227,218,.10)', border: '1px solid rgba(40,227,218,.22)', color: '#CFEFFF' }}>
                    البيانات تُعرض بطريقة تحمي الخصوصية (IP يظهر بشكل مخفي).
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(1,minmax(0,1fr))' : 'repeat(3,minmax(0,1fr))',
              gap: isMobile ? '12px' : '20px',
              width: '100%',
              maxWidth: '1108px',
              margin: '0 auto 22px',
            }}
          >
            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>المتواجدون الآن</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>🟢</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 700, fontSize: '20px' }}>
                  <CountUp value={cards.onlineNow || presence.onlineNow || 0} />
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', color: '#B8C5CE' }}>
                  من إجمالي {presence.totalTracked ? presence.totalTracked.toLocaleString('en-US') : '0'} جلسة
                </span>
              </div>

              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ fontFamily: 'Alexandria', fontSize: 11, color: ratioOnline > 0 ? '#19B228' : '#B8C5CE', textAlign: 'left' }}>
                  {ratioOnline > 0 ? `حوالي ${ratioOnline}% من الجلسات نشطة الآن` : 'لا يوجد نشاط ملحوظ الآن'}
                </div>
                <div style={{ width: 92, height: 10, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, ratioOnline))}%`, background: 'linear-gradient(135deg,#28E3DA,#7DF9E8)' }} />
                </div>
              </div>
            </div>

            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>زيارات الموقع ({rangeLabel})</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>📈</span>
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 700, fontSize: '20px' }}>
                  <CountUp value={cards.pageViews || 0} />
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', color: '#B8C5CE' }}>
                  زائر فريد: {cards.uniqueVisitors ? cards.uniqueVisitors.toLocaleString('en-US') : 0}
                </span>
              </div>

              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', fontFamily: 'Alexandria', fontSize: 11, color: '#B8C5CE' }}>
                <span>جدد: <span style={{ color: '#7DF9E8', fontWeight: 700 }}>{(cards.newVisitors || 0).toLocaleString('en-US')}</span></span>
                <span>عائدون: <span style={{ color: '#B1DFFF', fontWeight: 700 }}>{(cards.returningVisitors || 0).toLocaleString('en-US')}</span></span>
              </div>
            </div>

            <div style={metricCardBase} onMouseEnter={onMetricHoverIn} onMouseLeave={onMetricHoverOut}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF' }}>أكثر الأنظمة/الأجهزة/المتصفحات</span>
                <div style={{ width: 32, height: 32, borderRadius: '999px', background: 'rgba(8,20,35,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>🧭</span>
                </div>
              </div>

              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr', gap: 8, direction: 'rtl' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Alexandria', fontSize: 12, color: '#E8F5FF' }}>
                  <span style={{ color: '#B8C5CE' }}>OS</span>
                  <span>
                    {(presenceAnalytics.osTop[0]?.k || 'غير معروف')} <span style={{ color: '#7DF9E8' }}>({presenceAnalytics.osTop[0]?.v || 0})</span>
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Alexandria', fontSize: 12, color: '#E8F5FF' }}>
                  <span style={{ color: '#B8C5CE' }}>جهاز</span>
                  <span>
                    {(presenceAnalytics.deviceTop[0]?.k || 'غير معروف')} <span style={{ color: '#7DF9E8' }}>({presenceAnalytics.deviceTop[0]?.v || 0})</span>
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Alexandria', fontSize: 12, color: '#E8F5FF' }}>
                  <span style={{ color: '#B8C5CE' }}>متصفح</span>
                  <span>
                    {(presenceAnalytics.browserTop[0]?.k || 'غير معروف')} <span style={{ color: '#7DF9E8' }}>({presenceAnalytics.browserTop[0]?.v || 0})</span>
                  </span>
                </div>
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  onClick={exportPresenceCsv}
                  className="px-4 py-2 rounded-[14px] text-[12px] font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.45)',
                    background: 'rgba(40,227,218,.14)',
                    color: '#D0E3F0',
                  }}
                >
                  تصدير حضور CSV
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-6 px-4">
            <div
              className="flex items-center gap-2 px-2 py-2"
              style={{
                border: '1px solid rgba(40,227,218,.22)',
                background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                borderRadius: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                backdropFilter: 'blur(6px)',
              }}
              dir="rtl"
            >
              {[
                { key: 'week', label: 'أسبوع' },
                { key: 'month', label: 'شهر' },
                { key: 'quarter', label: '3 أشهر' },
                { key: 'year', label: 'سنة' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => handleTimeRangeChange(opt.key)}
                  className="px-4 py-2 rounded-[14px] font-bold flex items-center gap-2 transition-all"
                  style={{
                    color: timeRange === opt.key ? '#081423' : '#D0E3F0',
                    background: timeRange === opt.key ? 'linear-gradient(135deg,#28E3DA,#7DF9E8)' : 'transparent',
                    border: timeRange === opt.key ? '1px solid rgba(8,20,35,.6)' : '1px solid transparent',
                    boxShadow: timeRange === opt.key ? '0 0 18px rgba(40,227,218,.55)' : 'none',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div
            className="w-full max-w-5xl mx-auto mb-8"
            style={{
              borderRadius: '20px',
              border: '1px solid rgba(40,227,218,.22)',
              background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
              boxShadow: '0 8px 32px rgba(0,0,0,.35)',
              backdropFilter: 'blur(6px)',
              padding: '18px 18px 22px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: 15, fontWeight: 600 }}>نشاط الزوار على الموقع</span>
                <span style={{ fontFamily: 'Alexandria', fontSize: 11, color: '#B8C5CE' }}>({rangeLabel})</span>
              </div>
              <span style={{ fontFamily: 'Alexandria', fontSize: 11, color: '#B8C5CE' }}>
                {loading ? 'يتم التحديث…' : lastUpdatedAt ? `آخر تحديث: ${toValidDate(lastUpdatedAt)?.toLocaleString?.() || ''}` : 'آخر تحديث غير متوفر'}
              </span>
            </div>

            <div style={{ width: '100%', position: 'relative', overflow: 'hidden', borderRadius: '12px', background: 'rgba(6,14,26,.85)', border: '1px solid rgba(40,227,218,.10)' }}>
              <svg
                viewBox="0 0 700 360"
                width="100%"
                height={isMobile ? 200 : 260}
                preserveAspectRatio="none"
                style={{ display: 'block', cursor: 'crosshair' }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const mouseX = ((e.clientX - rect.left) / rect.width) * 700;
                  const pts = chartData.siteActivity;
                  if (!pts.length) return;
                  let nearestIdx = 0, nearestDist = Infinity;
                  pts.forEach((p, i) => { const d = Math.abs(LPX(p.x) - mouseX); if (d < nearestDist) { nearestDist = d; nearestIdx = i; } });
                  setHoveredPoint({ index: nearestIdx, x: pts[nearestIdx].x, y: pts[nearestIdx].y });
                }}
                onMouseLeave={() => setHoveredPoint(null)}
              >
                <defs>
                  <linearGradient id="lg-site" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#28E3DA" stopOpacity="0.35"/>
                    <stop offset="60%" stopColor="#28E3DA" stopOpacity="0.08"/>
                    <stop offset="100%" stopColor="#28E3DA" stopOpacity="0"/>
                  </linearGradient>
                  <clipPath id="log-clip">
                    <rect x="55" y="10" width="630" height="295"/>
                  </clipPath>
                </defs>

                {                       }
                {[0,1,2,3,4].map(i => (
                  <line key={i} x1="55" y1={10 + i*73} x2="685" y2={10 + i*73} stroke="rgba(255,255,255,.05)" strokeWidth="1"/>
                ))}

                {                     }
                <line x1="55" y1="305" x2="685" y2="305" stroke="rgba(255,255,255,.08)" strokeWidth="1"/>

                {                      }
                <g clipPath="url(#log-clip)">
                  {chartData.siteActivity.length > 1 && (
                    <>
                      <path d={smoothLineArea(chartData.siteActivity)} fill="url(#lg-site)"/>
                      <path d={smoothLinePath(chartData.siteActivity)} fill="none" stroke="#28E3DA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  )}

                  {                               }
                  {chartData.siteActivity.map((p, i) => (
                    <circle key={i} cx={LPX(p.x)} cy={LPY(p.y)} r="3" fill="#081423" stroke="#28E3DA" strokeWidth="1.5"/>
                  ))}
                </g>

                {                                     }
                {hoveredPoint !== null && chartData.siteActivity[hoveredPoint.index] && (() => {
                  const pt = chartData.siteActivity[hoveredPoint.index];
                  const hx = LPX(pt.x);
                  const hy = LPY(pt.y);
                  return (
                    <>
                      <line x1={hx} y1="10" x2={hx} y2="305" stroke="rgba(40,227,218,.25)" strokeWidth="1" strokeDasharray="4 3"/>
                      <circle cx={hx} cy={hy} r="8" fill="#28E3DA" opacity="0.18"/>
                      <circle cx={hx} cy={hy} r="5" fill="#28E3DA"/>
                      <circle cx={hx} cy={hy} r="2.5" fill="#fff"/>
                    </>
                  );
                })()}
              </svg>

              {                                                                     }
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                {                   }
                {[0,1,2,3,4].map(i => (
                  <div key={i} style={{
                    position: 'absolute',
                    top: `${((10 + i*73)/360)*100}%`,
                    left: 4,
                    transform: 'translateY(-50%)',
                    fontFamily: 'Alexandria',
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'rgba(148,163,184,.55)',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}>
                    {['100%','75%','50%','25%','0%'][i]}
                  </div>
                ))}

                {                   }
                {lXLabels.map((lbl, i) => (
                  <div key={i} style={{
                    position: 'absolute',
                    top: `${(326/360)*100}%`,
                    left: `${((55 + (i/6)*630)/700)*100}%`,
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Alexandria',
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'rgba(148,163,184,.6)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}>
                    {lbl}
                  </div>
                ))}

                {             }
                {hoveredPoint !== null && chartData.siteActivity[hoveredPoint.index] && (() => {
                  const pt = chartData.siteActivity[hoveredPoint.index];
                  const hx = LPX(pt.x);
                  const hy = LPY(pt.y);
                  const label = labelForPoint(hoveredPoint.index);
                  const pct = percentForPoint(pt.y);
                  const leftPct = Math.min(Math.max((hx/700)*100, 14), 86);
                  const topPct = Math.max(((hy - 70)/360)*100, 4);
                  return (
                    <div style={{
                      position: 'absolute',
                      top: `${topPct}%`,
                      left: `${leftPct}%`,
                      transform: 'translateX(-50%)',
                      background: 'rgba(6,14,26,.97)',
                      border: '1px solid rgba(40,227,218,.5)',
                      borderRadius: 10,
                      padding: '9px 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      direction: 'rtl',
                    }}>
                      <span style={{ fontFamily: 'Alexandria', fontSize: 11, fontWeight: 400, color: '#94a3b8', whiteSpace: 'nowrap' }}>{label}</span>
                      <span style={{ fontFamily: 'Alexandria', fontSize: 12, fontWeight: 700, color: '#7DF9E8', whiteSpace: 'nowrap' }}>نشاط: {pct}%</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="w-full max-w-5xl mx-auto px-4 mb-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4" style={{ direction: 'rtl' }}>
              <div className="flex flex-col">
                <span style={{ fontFamily: 'Alexandria', fontSize: 16, fontWeight: 600 }}>آخر المتواجدين في الموقع</span>
                <span style={{ fontFamily: 'Alexandria', fontSize: 12, color: '#B8C5CE' }}>
                  إجمالي الجلسات المتتبَّعة: {presence.totalTracked ? presence.totalTracked.toLocaleString('en-US') : 0} — المعروض: {filteredPresence.length.toLocaleString('en-US')}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={presenceQuery}
                  onChange={(e) => setPresenceQuery(e.target.value)}
                  placeholder="بحث: اسم / إيميل / دولة / جهاز / متصفح…"
                  className="px-4 py-2 rounded-[14px] text-[13px] outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#E8F5FF',
                    minWidth: isMobile ? '100%' : 320,
                    direction: 'rtl',
                  }}
                />

                <select
                  value={presenceType}
                  onChange={(e) => setPresenceType(e.target.value)}
                  className="px-3 py-2 rounded-[14px] text-[13px] font-bold outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#D0E3F0',
                    direction: 'rtl',
                  }}
                >
                  <option value="all">الكل</option>
                  <option value="registered">مسجلين</option>
                  <option value="guest">زوار</option>
                </select>

                <select
                  value={presenceSort}
                  onChange={(e) => setPresenceSort(e.target.value)}
                  className="px-3 py-2 rounded-[14px] text-[13px] font-bold outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#D0E3F0',
                    direction: 'rtl',
                  }}
                >
                  <option value="lastSeenDesc">الأحدث ظهوراً</option>
                  <option value="lastSeenAsc">الأقدم ظهوراً</option>
                </select>

                <select
                  value={presenceShowCount}
                  onChange={(e) => setPresenceShowCount(Number(e.target.value) || 10)}
                  className="px-3 py-2 rounded-[14px] text-[13px] font-bold outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#D0E3F0',
                    direction: 'rtl',
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <button
                  onClick={exportPresenceCsv}
                  className="px-4 py-2 rounded-[14px] text-[13px] font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.35)',
                    background: 'rgba(40,227,218,.14)',
                    color: '#D0E3F0',
                  }}
                >
                  تصدير
                </button>
              </div>
            </div>

            {presenceShown.length === 0 && !loading && (
              <div className="text-center text-[#B8C5CE] text-sm mb-6">
                لا توجد بيانات مطابقة للفلتر الحالي.
              </div>
            )}

            <div className="space-y-4">
              {presenceShown.map((session, idx) => {
                const vkey = session.vkey || `session-${idx}`;
                const isExpanded = expandedVkey === vkey;

                const name =
                  session.displayName ||
                  session.username ||
                  (session.email ? String(session.email).split('@')[0] : 'مستخدم مجهول');

                const email = session.email || '';
                const lastSeenText = timeAgoClient(session.lastSeen);

                const rawIp = session.ip || session.ipAddress || session.ip_address || '';
                const ipHash = session.ipHash || session.ip_hash || '';
                const ua = session.userAgent || session.user_agent || '';
                const uaInfo = parseUA(ua);

                const country = getCountry(session);
                const city = getCity(session);
                const region = getRegion(session);
                const isp = getISP(session);

                const registered = isRegisteredSession(session);
                const uid = effectiveUserId(session);

                const ipDisplay = displayIp(rawIp, ipHash);

                return (
                  <div
                    key={vkey}
                    className="p-5 transition-all hover:-translate-y-1"
                    style={{
                      background: isExpanded
                        ? 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.22) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.95)'
                        : 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                      border: isExpanded ? '1px solid rgba(40,227,218,.55)' : '1px solid rgba(40,227,218,.22)',
                      borderRadius: '20px',
                      boxShadow: '0 8px 32px rgba(0,0,0,.35)',
                      backdropFilter: 'blur(6px)',
                      direction: 'rtl',
                    }}
                  >
                    <div
                      className="flex justify-between items-start gap-3 mb-3 cursor-pointer"
                      onClick={() => setExpandedVkey((prev) => (prev === vkey ? null : vkey))}
                    >
                      <div className="flex flex-col">
                        <div className="text-[14px] font-medium text-white flex items-center gap-2">
                          <span>{name}</span>
                          <span
                            className="px-2 py-1 rounded-full text-[11px]"
                            style={{
                              background: registered ? 'rgba(40,227,218,.12)' : 'rgba(177,223,255,.10)',
                              border: `1px solid ${registered ? 'rgba(40,227,218,.30)' : 'rgba(177,223,255,.25)'}`,
                              color: registered ? '#7DF9E8' : '#B1DFFF',
                            }}
                          >
                            {registered ? 'مسجل' : 'زائر'}
                          </span>
                        </div>
                        {email && <div className="text-[12px] text-[#B8C5CE] ltr">{email}</div>}
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <div className="text-[12px] text-[#B8C5CE]">آخر ظهور: {lastSeenText || 'غير معروف'}</div>
                        <div className="text-[11px] text-[#7DE9FF]">
                          {country}{city ? ` - ${city}` : ''}{region ? ` (${region})` : ''}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 text-[13px] text-[#B8C5CE] mb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                          OS: <span className="ltr" style={{ color: '#E8F5FF' }}>{uaInfo.osLabel}</span>
                        </span>
                        <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                          جهاز: <span style={{ color: '#E8F5FF' }}>{uaInfo.deviceLabel}</span>
                        </span>
                        <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                          متصفح: <span className="ltr" style={{ color: '#E8F5FF' }}>{uaInfo.browserName}</span>
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          className="px-3 py-1 rounded-[10px] border text-[11px]"
                          style={{ borderColor: 'rgba(40,227,218,.25)', background: 'transparent', color: '#D0E3F0' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedVkey((prev) => (prev === vkey ? null : vkey));
                          }}
                        >
                          {isExpanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
                        </button>

                        <button
                          className="px-3 py-1 rounded-[10px] border text-[11px]"
                          style={{ borderColor: 'rgba(40,227,218,.25)', background: 'transparent', color: '#D0E3F0' }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await safeCopy(email || name);
                          }}
                        >
                          نسخ الاسم/الإيميل
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-[rgba(40,227,218,.25)] text-[13px] text-[#E8F5FF]">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">الاسم</span>
                            <span>{name}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">البريد الإلكتروني</span>
                            <span className="ltr">{email || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">نوع الجلسة</span>
                            <span>{registered ? 'جلسة مستخدم مسجَّل' : 'جلسة زائر (غير مسجَّل)'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">userId</span>
                            <span className="ltr">{uid ? String(uid) : 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">آخر ظهور</span>
                            <span>{lastSeenText || 'غير معروف'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">الوقت الفعلي</span>
                            <span className="ltr">{toValidDate(session.lastSeen)?.toLocaleString() || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">عنوان IP</span>
                            <span className="ltr" style={{ color: rawIp ? '#7DF9E8' : '#94a3b8' }}>{rawIp || 'غير متوفر (سجل قديم)'}</span>
                          </div>

                          {ipHash && (
                            <div className="flex justify-between items-center">
                              <span className="text-[#B8C5CE]">IP Hash</span>
                              <span className="ltr" style={{ fontSize: 11, color: '#64748b' }}>{ipHash}</span>
                            </div>
                          )}

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">الدولة</span>
                            <span>{country || 'غير معروفة'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">المنطقة</span>
                            <span>{region || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">المدينة</span>
                            <span>{city || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">مقدم الخدمة (ISP)</span>
                            <span>{isp || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">نظام التشغيل</span>
                            <span className="ltr">{uaInfo.osFull}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">نوع الجهاز</span>
                            <span>{uaInfo.deviceFull}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">المتصفح</span>
                            <span className="ltr">{uaInfo.browserFull}</span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-[#B8C5CE]">معرّف الجلسة</span>
                            <span className="ltr">{String(ipHash || vkey || '').slice(0, 48) || 'غير متوفر'}</span>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            className="px-4 py-2 rounded-[14px] text-[12px] font-bold"
                            style={{ border: '1px solid rgba(40,227,218,.35)', background: 'rgba(40,227,218,.12)', color: '#D0E3F0' }}
                            onClick={async () => {
                              await safeCopy(rawIp || ipHash || vkey || '');
                            }}
                          >
                            نسخ عنوان IP
                          </button>

                          <button
                            className="px-4 py-2 rounded-[14px] text-[12px] font-bold"
                            style={{ border: '1px solid rgba(40,227,218,.35)', background: 'rgba(40,227,218,.12)', color: '#D0E3F0' }}
                            onClick={async () => {
                              await safeCopy(ua || '');
                            }}
                          >
                            نسخ User-Agent
                          </button>
                        </div>

                        {ua && (
                          <div className="mt-3 text-[12px] text-[#B8C5CE]">
                            <div className="ltr break-words">{ua}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredPresence.length > presenceShown.length && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => setPresenceShowCount((n) => Math.min(filteredPresence.length, n + 20))}
                  className="px-6 py-2 rounded-[14px] text-sm font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.35)',
                    background: 'rgba(40,227,218,.14)',
                    color: '#D0E3F0',
                  }}
                >
                  عرض المزيد من الحضور
                </button>
              </div>
            )}
          </div>

          <div className="w-full max-w-5xl mx-auto px-4 mb-16">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4" style={{ direction: 'rtl' }}>
              <div className="flex flex-col">
                <span style={{ fontFamily: 'Alexandria', fontSize: 16, fontWeight: 600 }}>سجل الزيارات التفصيلي (site_events)</span>
                <span style={{ fontFamily: 'Alexandria', fontSize: 12, color: '#B8C5CE' }}>
                  المعروض (بعد الفلتر): {filteredEvents.length.toLocaleString('en-US')} — المحمّل: {events.length.toLocaleString('en-US')}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={eventsQuery}
                  onChange={(e) => setEventsQuery(e.target.value)}
                  placeholder="بحث: صفحة / دولة / جهاز / متصفح / مستخدم…"
                  className="px-4 py-2 rounded-[14px] text-[13px] outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#E8F5FF',
                    minWidth: isMobile ? '100%' : 320,
                    direction: 'rtl',
                  }}
                />

                <select
                  value={eventsType}
                  onChange={(e) => setEventsType(e.target.value)}
                  className="px-3 py-2 rounded-[14px] text-[13px] font-bold outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#D0E3F0',
                    direction: 'rtl',
                  }}
                >
                  <option value="all">كل الأنواع</option>
                  {eventsTypesSet.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>

                <select
                  value={eventsUserType}
                  onChange={(e) => setEventsUserType(e.target.value)}
                  className="px-3 py-2 rounded-[14px] text-[13px] font-bold outline-none"
                  style={{
                    border: '1px solid rgba(40,227,218,.25)',
                    background: 'rgba(9,28,43,.65)',
                    color: '#D0E3F0',
                    direction: 'rtl',
                  }}
                >
                  <option value="all">الكل</option>
                  <option value="registered">مسجلين</option>
                  <option value="guest">زوار</option>
                </select>

                <button
                  onClick={exportEventsCsv}
                  className="px-4 py-2 rounded-[14px] text-[13px] font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.35)',
                    background: 'rgba(40,227,218,.14)',
                    color: '#D0E3F0',
                  }}
                >
                  تصدير
                </button>

                <button
                  disabled={eventsLoading}
                  onClick={() => fetchEvents(0)}
                  className="px-4 py-2 rounded-[14px] text-[13px] font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.35)',
                    background: eventsLoading ? 'rgba(40,227,218,.06)' : 'transparent',
                    color: '#D0E3F0',
                    opacity: eventsLoading ? 0.7 : 1,
                  }}
                >
                  تحديث السجل
                </button>
              </div>
            </div>

            {filteredEvents.length === 0 && !eventsLoading && (
              <div className="text-center text-[#B8C5CE] text-sm mb-4">لا توجد زيارات مطابقة للفلتر الحالي.</div>
            )}

            <div className="space-y-3">
              {filteredEvents.map((evt) => {
                const createdAtText = timeAgoClient(evt.createdAt);
                const createdFull = toValidDate(evt.createdAt)?.toLocaleString() || '';
                const ua = evt.userAgent || evt.user_agent || '';
                const uaInfo = parseUA(ua);

                const rawIp = evt.ip || evt.ipAddress || evt.ip_address || '';
                const ipHash = evt.ipHash || evt.ip_hash || '';
                const ipDisplay = displayIp(rawIp, ipHash);

                const country = getCountry(evt);
                const city = getCity(evt);
                const region = getRegion(evt);
                const isp = getISP(evt);

                const evtUserId = evt.userId || evt.user_id || null;
                const evtEmail = evt.email || evt.userEmail || '';
                const evtUsername = evt.username || evt.displayName || '';
                const evtIsRegistered = Boolean(evtUserId || evtEmail || evtUsername);

                const isExpanded = expandedEventId === evt.id;

                return (
                  <div
                    key={`evt-${evt.id}`}
                    className="p-4 transition-all hover:-translate-y-0.5"
                    style={{
                      borderColor: isExpanded ? 'rgba(40,227,218,.55)' : 'rgba(40,227,218,.22)',
                      border: `1px solid ${isExpanded ? 'rgba(40,227,218,.55)' : 'rgba(40,227,218,.22)'}`,
                      background: isExpanded
                        ? 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.22) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.95)'
                        : 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
                      borderRadius: '18px',
                      boxShadow: '0 4px 20px rgba(0,0,0,.3)',
                      backdropFilter: 'blur(6px)',
                      direction: 'rtl',
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13px] text-[#B8C5CE]">
                          نوع الحدث: <span className="text-white">{evt.eventType}</span>
                        </span>
                        <span
                          className="px-2 py-1 rounded-full text-[11px]"
                          style={{
                            background: evtIsRegistered ? 'rgba(40,227,218,.12)' : 'rgba(177,223,255,.10)',
                            border: `1px solid ${evtIsRegistered ? 'rgba(40,227,218,.30)' : 'rgba(177,223,255,.25)'}`,
                            color: evtIsRegistered ? '#7DF9E8' : '#B1DFFF',
                          }}
                        >
                          {evtIsRegistered ? 'مسجل' : 'زائر'}
                        </span>
                        <span className="text-[12px] text-[#7DE9FF]">
                          {country}{city ? ` - ${city}` : ''}{region ? ` (${region})` : ''}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 justify-end">
                        <div className="text-[12px] text-[#B8C5CE]">{createdAtText || 'منذ قليل'}</div>
                        <button
                          className="px-3 py-1 rounded-[10px] border text-[11px]"
                          style={{ borderColor: 'rgba(40,227,218,.25)', background: 'transparent', color: '#D0E3F0' }}
                          onClick={() => setExpandedEventId((p) => (p === evt.id ? null : evt.id))}
                        >
                          {isExpanded ? 'إخفاء التفاصيل' : 'تفاصيل'}
                        </button>
                        <button
                          className="px-3 py-1 rounded-[10px] border text-[11px]"
                          style={{ borderColor: 'rgba(40,227,218,.25)', background: 'transparent', color: '#D0E3F0' }}
                          onClick={async () => {
                            await safeCopy(evt.pagePath || evt.pageTitle || '');
                          }}
                        >
                          نسخ مسار الصفحة
                        </button>
                      </div>
                    </div>

                    <div className="text-[13px] text-white mb-1">
                      الصفحة: <span className="text-[#B1DFFF]">{evt.pageTitle || evt.pagePath || 'غير معروفة'}</span>
                    </div>
                    <div className="text-[12px] text-[#B8C5CE] mb-2 ltr break-words">{evt.pagePath || ''}</div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                        OS: <span className="ltr" style={{ color: '#E8F5FF' }}>{uaInfo.osLabel}</span>
                      </span>
                      <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                        جهاز: <span style={{ color: '#E8F5FF' }}>{uaInfo.deviceLabel}</span>
                      </span>
                      <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                        متصفح: <span className="ltr" style={{ color: '#E8F5FF' }}>{uaInfo.browserName}</span>
                      </span>
                      <span className="px-2 py-1 rounded-full text-[11px]" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
                        IP: <span className="ltr" style={{ color: ipDisplay ? '#7DF9E8' : '#64748b' }}>{ipDisplay || 'غير متوفر'}</span>
                      </span>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-[rgba(40,227,218,.18)]">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[12px] text-[#E8F5FF]">
                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">الدولة</span>
                            <span>{country || 'غير معروفة'}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">المدينة</span>
                            <span>{city || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">المنطقة</span>
                            <span>{region || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">ISP</span>
                            <span>{isp || 'غير متوفر'}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">عنوان IP</span>
                            <span className="ltr" style={{ color: rawIp ? '#7DF9E8' : '#94a3b8' }}>{rawIp || 'غير متوفر (سجل قديم)'}</span>
                          </div>

                          {ipHash && (
                            <div className="flex justify-between">
                              <span className="text-[#B8C5CE]">IP Hash</span>
                              <span className="ltr" style={{ fontSize: 11, color: '#64748b' }}>{ipHash}</span>
                            </div>
                          )}

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">نظام التشغيل</span>
                            <span className="ltr">{uaInfo.osFull}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">نوع الجهاز</span>
                            <span>{uaInfo.deviceFull}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">المتصفح</span>
                            <span className="ltr">{uaInfo.browserFull}</span>
                          </div>

                          <div className="flex justify-between">
                            <span className="text-[#B8C5CE]">الوقت الفعلي</span>
                            <span className="ltr">{createdFull}</span>
                          </div>

                          {evtUserId && (
                            <div className="flex justify-between">
                              <span className="text-[#B8C5CE]">userId</span>
                              <span className="ltr">{String(evtUserId)}</span>
                            </div>
                          )}

                          {(evtEmail || evtUsername) && (
                            <div className="flex justify-between">
                              <span className="text-[#B8C5CE]">المستخدم</span>
                              <span className="ltr">{evtEmail || evtUsername}</span>
                            </div>
                          )}
                        </div>

                        {evt.referrer && <div className="mt-2 text-[11px] text-[#B8C5CE] ltr break-words">referrer: {evt.referrer}</div>}
                        {ua && <div className="mt-2 text-[11px] text-[#B8C5CE] ltr break-words">{ua}</div>}

                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            className="px-4 py-2 rounded-[14px] text-[12px] font-bold"
                            style={{ border: '1px solid rgba(40,227,218,.35)', background: 'rgba(40,227,218,.12)', color: '#D0E3F0' }}
                            onClick={async () => {
                              await safeCopy(rawIp || ipHash || '');
                            }}
                          >
                            نسخ عنوان IP
                          </button>

                          <button
                            className="px-4 py-2 rounded-[14px] text-[12px] font-bold"
                            style={{ border: '1px solid rgba(40,227,218,.35)', background: 'rgba(40,227,218,.12)', color: '#D0E3F0' }}
                            onClick={async () => {
                              await safeCopy(ua || '');
                            }}
                          >
                            نسخ User-Agent
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center mt-4">
              {eventsHasMore ? (
                <button
                  disabled={eventsLoading}
                  onClick={() => fetchEvents(eventsPage + 1)}
                  className="px-6 py-2 rounded-[14px] text-sm font-bold"
                  style={{
                    border: '1px solid rgba(40,227,218,.6)',
                    background: eventsLoading ? 'rgba(40,227,218,.2)' : 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
                    color: '#081423',
                    boxShadow: '0 0 18px rgba(40,227,218,.55)',
                    opacity: eventsLoading ? 0.7 : 1,
                  }}
                >
                  {eventsLoading ? 'جاري تحميل المزيد…' : 'عرض المزيد من الزيارات'}
                </button>
              ) : (
                events.length > 0 && <div className="text-center text-[#B8C5CE] text-xs mt-2">تم عرض كل الزيارات المتاحة.</div>
              )}
            </div>
          </div>
        </div>

        <MobileBottomNav />
      </div>
    </div>
  );
};

export default ActivityLogPage;
